// NavBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import './NavBar.css';
import logotext from '../../assets/svgs/iwinto.png';
import LogoCircular from '../../assets/svgs/logo circular1.png';
import { FiMoreVertical, FiX, FiArrowLeft } from 'react-icons/fi';
import { motion } from 'framer-motion';
import DownloadModal from '../Buttons/DownloadModal/DownloadModal';
// Supabase
import { supabase } from '../../supabase/supabase';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import TextLanguageSwitcher from '../TextLanguageSwitcher/TextLanguageSwitcher';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { FiMail, FiPhone, FiChevronLeft } from 'react-icons/fi';

function NavBar({ activeSection }) {
  const { translations: t, language } = useLanguage();
  const { currentUser, userProfile, signOut } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  // Mobile menu
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Navbar hide/show
  const [scrolled, setScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [visible, setVisible] = useState(true);

  // Auth dropdown
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Auth form state
  const [signInMethod, setSignInMethod] = useState(''); // 'email' | 'phone' | ''
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [authError, setAuthError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Profile dropdown
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  // Toggle mobile hamburger
  const toggleMenu = () => {
    setIsMenuOpen(prev => {
      const newVal = !prev;
      document.body.style.overflow = newVal ? 'hidden' : 'auto';
      // if opening menu, also close auth
      if (newVal) {
        setIsAuthOpen(false);
        setSignInMethod(''); // Reset method
      }
      return newVal;
    });
  };

  // Close menus on route change
  useEffect(() => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  }, [location.pathname]);

  // Navbar hide/show on scroll
  useEffect(() => {
    function handleScroll() {
      const y = window.scrollY;
      setScrolled(y > 10);

      // only auto–hide/show on very small screens
      if (window.innerWidth <= 900) {
        if (y > lastScrollY) {
          // scrolling down → hide
          if (visible) setVisible(false);
        } else if (y < lastScrollY) {
          // scrolling up → show
          if (!visible) setVisible(true);
        }
      }

      // remember position for next event
      setLastScrollY(y);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, visible]);

  // Handle OTP Sending
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setAuthError('');
    setIsSubmitting(true);

    try {
      let endpoint = '';
      let body = {};
      const method = signInMethod === 'email' ? 'email' : 'sms'; // Default to sms for phone, can extend to whatsapp later if needed

      if (signInMethod === 'email') {
        if (!email) throw new Error('Email is required');
        endpoint = 'email-login-otp-send';
        body = { email };
      } else if (signInMethod === 'phone') {
        if (!phoneNumber) throw new Error('Phone number is required');
        endpoint = 'phone-login-otp-send';
        body = { phoneNumber, method: 'sms' }; // Using SMS by default as per request
      } else {
        return;
      }

      console.log(`Sending OTP via ${signInMethod} to`, body);

      const { data, error } = await supabase.functions.invoke(endpoint, { body });

      if (error) throw error;

      // Navigate to verification page
      setIsAuthOpen(false);
      navigate('/otp-verification', {
        state: {
          email: signInMethod === 'email' ? email : null,
          phoneNumber: signInMethod === 'phone' ? phoneNumber : null,
          method: signInMethod === 'email' ? 'email' : 'sms'
        }
      });

    } catch (err) {
      console.error('OTP Send Error:', err);
      setAuthError(err.message || 'Failed to send verification code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (isSubmitting) return;
    setAuthError('');
    try {
      setIsSubmitting(true);
      const postAuthPath = '/feed'; // Simplified, always feed for now
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${postAuthPath}`,
          queryParams: {
            prompt: 'select_account',
          },
          skipBrowserRedirect: false
        }
      });
      if (error) {
        console.error('Google OAuth error:', error);
        throw error;
      }
      // The redirect will happen automatically, no need to navigate manually
    } catch (err) {
      console.error('Google sign-in error:', err);
      setAuthError(err.message || 'Google sign-in failed. Please check your Supabase configuration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAppleLogin = async () => {
    if (isSubmitting) return;
    setAuthError('');
    try {
      setIsSubmitting(true);
      const postAuthPath = '/feed'; // Simplified
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}${postAuthPath}`,
          queryParams: {
            response_mode: 'form_post'
          },
          skipBrowserRedirect: false
        }
      });
      if (error) {
        console.error('Apple OAuth error:', error);
        throw error;
      }
      // The redirect will happen automatically, no need to navigate manually
    } catch (err) {
      console.error('Apple sign-in error:', err);
      setAuthError(err.message || 'Apple sign-in failed. Please check your Supabase configuration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Profile dropdown handlers
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(prev => !prev);
  };

  const handleSignOut = async () => {
    console.log('[NavBar] Signing out...');
    try {
      await signOut();
      setIsProfileDropdownOpen(false);
      navigate('/');
    } catch (error) {
      console.error('[NavBar] Sign out error:', error);
    }
  };

  const handleFeedClick = () => {
    if (currentUser) {
      navigate('/feed');
    }
    setIsProfileDropdownOpen(false);
  };

  const handleProfileHeaderClick = () => {
    if (currentUser) {
      navigate('/profile');
    }
    setIsProfileDropdownOpen(false);
  };

  // Close auth dropdown on outside click and listen for footer auth trigger
  useEffect(() => {
    function handleClickOutside(e) {
      if (isAuthOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsAuthOpen(false);
      }
      if (isProfileDropdownOpen && profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setIsProfileDropdownOpen(false);
      }
    }

    function handleFooterAuthTrigger(e) {
      if (e.detail?.source === 'footer') {
        setIsAuthOpen(true);
        setAuthError('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('openNavbarAuth', handleFooterAuthTrigger);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('openNavbarAuth', handleFooterAuthTrigger);
    };
  }, [isAuthOpen, isProfileDropdownOpen]);

  // Determine if we’re on the landing “Discover iWinto” section
  const isLanding = activeSection === 2 || currentPath === '/landing';
  const scrollToLandingAnd = id => {
    const outer = document.querySelector('.scroll-container');
    if (outer) outer.scrollTo({ top: 2 * window.innerHeight, behavior: 'smooth' });
    setTimeout(() => {
      const tgt = document.getElementById(id);
      if (tgt) tgt.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  };

  // translation dir
  useEffect(() => {
    document.body.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  // Toggle auth dropdown
  const toggleAuth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAuthOpen(prev => {
      if (prev) {
        setAuthError('');
        setSignInMethod('');
        setEmail('');
        setPhoneNumber('');
        return false;
      }
      setSignInMethod('');
      setEmail('');
      setPhoneNumber('');
      return true;
    });
  };

  // Removed switchToSignUp and switchToSignIn as they are no longer needed


  return (
    <motion.nav
      className={`navbar-container ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: visible ? 0 : -100 }}
      transition={{ duration: 0.3 }}
    >
      <div className="navbar-content">
        {/* Left: Logo */}
        <motion.div
          className="navbar-left"
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 400, damping: 10 }}
        >
          <img
            src={logotext}
            alt="iwinto"
            className="logo-img-text"
            onClick={() => {
              if (currentPath === "/") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
            style={{ cursor: "pointer" }}
          />
        </motion.div>

        {/* Center links */}
        <div className={`navbar-center ${isMenuOpen ? 'active' : ''}`}>
          <ul>
            {isLanding ? (
              <>
                <motion.li whileHover={{ scale: 1.05 }}><a href="#features" onClick={e => { e.preventDefault(); scrollToLandingAnd('features'); }}>Features</a></motion.li>
                <motion.li whileHover={{ scale: 1.05 }}><a href="#business" onClick={e => { e.preventDefault(); scrollToLandingAnd('business'); }}>Business</a></motion.li>
                <motion.li whileHover={{ scale: 1.05 }}><a href="#community" onClick={e => { e.preventDefault(); scrollToLandingAnd('community'); }}>Community</a></motion.li>
                <motion.li whileHover={{ scale: 1.05 }}><a href="#affiliation-cards" onClick={e => { e.preventDefault(); scrollToLandingAnd('affiliation-cards'); }}>Affiliation Cards</a></motion.li>
                <motion.li whileHover={{ scale: 1.05 }}><a href="#categories" onClick={e => { e.preventDefault(); scrollToLandingAnd('categories'); }}>Categories</a></motion.li>
              </>
            ) : (
              <>
                {isMenuOpen && (
                  <div className="mobile-language-switcher">
                    <TextLanguageSwitcher />
                  </div>
                )}

                <motion.li whileHover={{ scale: 1.05 }}>
                  <NavLink to="/about" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => (isActive ? 'active-link' : undefined)}>
                    {t.navbar?.links?.about || "About"}
                  </NavLink>
                </motion.li>

                <motion.li whileHover={{ scale: 1.05 }}>
                  <NavLink to="/contact" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => (isActive ? 'active-link' : undefined)}>
                    {t.navbar?.links?.contact || "Contact"}
                  </NavLink>
                </motion.li>

                <motion.li whileHover={{ scale: 1.05 }}>
                  <NavLink to="/faq" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => (isActive ? 'active-link' : undefined)}>
                    {t.navbar?.links?.faq || "FAQ's"}
                  </NavLink>
                </motion.li>

                <motion.li whileHover={{ scale: 1.05 }}>
                  <NavLink to="/privacy-policy" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => (isActive ? 'active-link' : undefined)}>
                    {t.navbar?.links?.privacy || "Privacy Policy"}
                  </NavLink>
                </motion.li>

                <motion.li whileHover={{ scale: 1.05 }}>
                  <NavLink to="/terms-of-services" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => (isActive ? 'active-link' : undefined)}>
                    {t.navbar?.links?.terms || "Terms of Services"}
                  </NavLink>
                </motion.li>

                <motion.li whileHover={{ scale: 1.05 }}>
                  <NavLink to="/investment" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => (isActive ? 'active-link' : undefined)}>
                    {t.navbar?.links?.investment || "Investment"}
                  </NavLink>
                </motion.li>
              </>
            )}
          </ul>
        </div>

        {/* Right: Auth/Profile */}
        <div className="navbar-right">
          <DownloadModal />

          {currentUser ? (
            // Show profile image when authenticated
            <div className="profile-toggle-container" ref={profileDropdownRef}>
              <button
                className="profile-toggle"
                onClick={toggleProfileDropdown}
                onMouseDown={(e) => e.preventDefault()}
                aria-expanded={isProfileDropdownOpen}
                aria-controls="profile-dropdown"
              >
                <img
                  src={userProfile?.avatar_url || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K"}
                  alt={userProfile?.display_name || currentUser?.user_metadata?.full_name || currentUser?.user_metadata?.name || "Profile"}
                  className="profile-image"
                />
              </button>

              {isProfileDropdownOpen && (
                <div className="profile-dropdown">
                  <div className="profile-header" onClick={handleProfileHeaderClick} style={{ cursor: 'pointer' }}>
                    <img
                      src={userProfile?.avatar_url || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K"}
                      alt={userProfile?.display_name || currentUser?.user_metadata?.full_name || currentUser?.user_metadata?.name || "Profile"}
                      className="profile-dropdown-image"
                    />
                    <div className="profile-info">
                      <h3>{userProfile?.display_name || currentUser?.user_metadata?.full_name || currentUser?.user_metadata?.name || 'User'}</h3>
                      <p>{userProfile?.email || currentUser?.email}</p>
                    </div>
                  </div>

                  <div className="profile-actions">
                    <button onClick={handleFeedClick} className="profile-action-btn">
                      {t?.navbarAvatar?.feed || 'Feed'}
                    </button>
                    <button onClick={handleSignOut} className="profile-action-btn signout-btn">
                      {t?.navbarAvatar?.signOut || 'Sign Out'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Show sign in button when not authenticated
            <div className="auth-wrapper">
              <button
                className="sign-in-toggle"
                onClick={toggleAuth}
                onMouseDown={(e) => e.preventDefault()}
                aria-expanded={isAuthOpen}
                aria-controls="signin-dropdown"
              >
                {t.navbar?.auth?.signIn || "Sign In"}
              </button>

              <div
                ref={dropdownRef}
                id="signin-dropdown"
                className={`signin-dropdown ${isAuthOpen ? 'open' : ''}`}
              >
                {/* Back arrow inside the panel */}
                <button
                  className="signin-back"
                  onClick={() => setIsAuthOpen(false)}
                  aria-label="Back"
                >
                  <FiArrowLeft size={20} />
                </button>

                <div className="auth-form-container">
                  <div className="auth-form-wrapper">
                    <div className="auth-header">
                      <div className="logo-border-radius">
                        <img src={LogoCircular} alt="logo" />
                      </div>
                      <h1>
                        {signInMethod === 'email' && "Sign in with Email"}
                        {signInMethod === 'phone' && "Sign in with Phone"}
                        {!signInMethod && (t.navbar?.auth?.welcome || "Welcome to iWinto")}
                      </h1>
                    </div>

                    {/* Main Selection View */}
                    {!signInMethod && (
                      <div className="auth-method-selection">
                        <button onClick={() => setSignInMethod('email')} className="auth-method-btn email-btn">
                          <FiMail className="auth-icon" />
                          Sign in with Email
                        </button>

                        <button onClick={() => setSignInMethod('phone')} className="auth-method-btn phone-btn">
                          <FiPhone className="auth-icon" />
                          Sign in with Phone
                        </button>

                        <div className="auth-divider"><span>Or Continue With</span></div>

                        <div className="social-buttons-vertical">
                          <button onClick={handleGoogleLogin} className="google-button" disabled={isSubmitting}>
                            <img src="https://www.google.com/favicon.ico" alt="Google" className="google-icon" />
                            Google
                          </button>
                          <button onClick={handleAppleLogin} className="apple-button" disabled={isSubmitting}>
                            <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="Apple" className="apple-icon" />
                            Apple
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Email Form */}
                    {signInMethod === 'email' && (
                      <form onSubmit={handleSendOtp} className="auth-form">
                        <button
                          type="button"
                          className="back-method-btn"
                          onClick={() => {
                            setSignInMethod('');
                            setAuthError('');
                          }}
                        >
                          <FiChevronLeft /> Back
                        </button>

                        <div className="form-group">
                          <input
                            type="email"
                            placeholder={t.navbar?.auth?.emailPlaceholder || "Email"}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                            autoFocus // Automatically focus when switching
                          />
                        </div>
                        <button type="submit" className="auth-button" disabled={isSubmitting}>
                          {isSubmitting ? 'Sending...' : 'Continue'}
                        </button>
                        {!!authError && <p className="auth-error">{authError}</p>}
                      </form>
                    )}

                    {/* Phone Form */}
                    {signInMethod === 'phone' && (
                      <form onSubmit={handleSendOtp} className="auth-form">
                        <button
                          type="button"
                          className="back-method-btn"
                          onClick={() => {
                            setSignInMethod('');
                            setAuthError('');
                          }}
                        >
                          <FiChevronLeft /> Back
                        </button>

                        <div className="form-group phone-group">
                          <PhoneInput
                            placeholder="Enter phone number"
                            value={phoneNumber}
                            onChange={setPhoneNumber}
                            defaultCountry="US"
                            international
                            withCountryCallingCode
                          />
                        </div>
                        <button type="submit" className="auth-button" disabled={isSubmitting}>
                          {isSubmitting ? 'Sending...' : 'Send Code'}
                        </button>
                        {!!authError && <p className="auth-error">{authError}</p>}
                      </form>
                    )}

                    <div className="auth-footer">
                      <p>
                        {t.navbar?.auth?.termsText || "By continuing, you agree to iWinto's"}{' '}
                        <Link to="/terms-of-services" target="_blank">{t.navbar?.links?.terms || "Terms of Service"}</Link>{' '}
                        {t.navbar?.auth?.andText || "and acknowledge you've read our"}{' '}
                        <Link to="/privacy-policy" target="_blank">{t.navbar?.links?.privacy || "Privacy Policy"}</Link>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <LanguageSwitcher />
        </div>

        {/* Mobile menu button */}
        <motion.button
          className="mobile-menu-btn"
          onClick={toggleMenu}
          whileTap={{ scale: 0.9 }}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <FiX size={28} /> : <FiMoreVertical size={28} />}
        </motion.button>
      </div>
    </motion.nav>
  );
}

export default NavBar;
